import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, RefreshCw, ChevronRight, ChevronDown, Target, SlidersHorizontal, X } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

const money = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v || 0);
  // Rule 10: a negative amount reads "−₹780", never "₹-780"
  return `${n < 0 ? "−" : ""}₹${Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};
const GRADE = {
  Healthy: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Watch: "bg-amber-100 text-amber-800 border-amber-300",
  Critical: "bg-orange-100 text-orange-800 border-orange-300",
  Dead: "bg-red-100 text-red-800 border-red-300",
};
const CHIPS = [
  ["not_delivered", "Not delivered"],
  ["expiring_3", "Expiring in 3 days"],
  ["no_appointment", "No appointment"],
  ["appointment_missed", "Appointment missed"],
  ["short_grn", "Short GRN"],
  ["not_paid", "Not paid yet"],
];
const FKEYS = ["date_from", "date_to", "ss_ids", "city", "facility", "item", "q", "status",
  "appt_from", "appt_to", "expiry_from", "expiry_to", "grn_from", "grn_to", "value_min",
  "value_max", "fill_min", "fill_max", "invoice_status", "payment_status", "grade", "chips",
  "show_closed_ss"];
const EMPTY = FKEYS.reduce((a, k) => ({ ...a, [k]: "" }), {});

function Card({ label, value, sub, tone = "text-zinc-950", testId }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testId}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">{label}</p>
      <p className={`font-mono text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-400 mt-1">{sub}</p>}
    </div>
  );
}

function Pager({ data, page, setPage, testId }) {
  if (!data) return null;
  return (
    <div className="flex items-center justify-between py-2 px-1" data-testid={testId}>
      <span className="text-[11px] text-zinc-500" data-testid={`${testId}-showing`}>{data.showing}</span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]" disabled={page <= 1}
          onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}>Previous</Button>
        <span className="text-[11px] text-zinc-500 px-2 self-center">Page {data.page} of {data.pages}</span>
        <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]" disabled={page >= data.pages}
          onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}>Next</Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{label}</label>
    {children}
  </div>
);
const inputCls = "h-8 text-xs border border-zinc-200 rounded-sm px-2 bg-white";

export default function QcomSSPOs({ platform = "", canManageTargets = false }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("qtab") || "pos");
  const [widgets, setWidgets] = useState(null);
  const [pos, setPos] = useState(null);
  const [score, setScore] = useState(null);
  const [options, setOptions] = useState({ super_stockists: [], cities: [], facilities: [], items: [] });
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(false);
  const [sortBy, setSortBy] = useState("po_date");
  const [sortDir, setSortDir] = useState("desc");
  const [scSort, setScSort] = useState({ by: "ordered", dir: "desc" });
  const [page, setPage] = useState(Number(sp.get("qpage") || 1));
  const [pageSize, setPageSize] = useState(Number(sp.get("qsize") || 25));
  const [open, setOpen] = useState({});
  const [showTargets, setShowTargets] = useState(false);
  const [tForm, setTForm] = useState({ fill_target: 85, appointment_target: 90, dn_pct_ceiling: 2, watch_from: 60 });
  // item 9(c) — assigning an unmatched platform vendor name straight from the banner
  const [assign, setAssign] = useState({});
  const [assigning, setAssigning] = useState("");
  // PASS 2 ZIP 1 FIX-UP (Fix 1d): the warehouse-map confirm for a Needs-mapping row
  const [nmConfirm, setNmConfirm] = useState(null);
  const [sendingAlert, setSendingAlert] = useState(false);

  // Q3 — the filter bar lives in the page URL, so Back and a copied link keep it
  const f = useMemo(() => {
    const out = { ...EMPTY };
    FKEYS.forEach(k => { out[k] = sp.get(k) || ""; });
    return out;
  }, [sp]);
  const can = widgets?.can || pos?.can || {};

  const setF = (patch, resetPage = true) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { v ? next.set(k, v) : next.delete(k); });
    if (resetPage) { next.set("qpage", "1"); setPage(1); }
    setSp(next, { replace: false });
  };
  const clearFilters = () => {
    const next = new URLSearchParams(sp);
    FKEYS.forEach(k => next.delete(k));
    next.set("qpage", "1"); setPage(1);
    setSp(next);
  };
  const toggleChip = (key) => {
    const cur = f.chips ? f.chips.split(",") : [];
    const next = cur.includes(key) ? cur.filter(c => c !== key) : [...cur, key];
    setF({ chips: next.join(",") });
  };
  const toggleSs = (id) => {
    const cur = f.ss_ids ? f.ss_ids.split(",") : [];
    const next = cur.includes(id) ? cur.filter(c => c !== id) : [...cur, id];
    setF({ ss_ids: next.join(",") });
  };

  const params = useMemo(() => {
    const p = { platform };
    FKEYS.forEach(k => { if (f[k]) p[k] = f[k]; });
    return p;
  }, [platform, f]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, p, s, o] = await Promise.all([
        api.get("/qcom-ss/widgets", { params }),
        api.get("/qcom-ss/pos", { params: { ...params, sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize } }),
        api.get("/qcom-ss/scorecard", { params: { ...params, sort_by: scSort.by, sort_dir: scSort.dir } }),
        api.get("/qcom-ss/filter-options", { params: { platform, date_from: f.date_from, date_to: f.date_to } }),
      ]);
      setWidgets(w.data); setPos(p.data); setScore(s.data); setOptions(o.data);
      setTForm(s.data.targets);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the Qcom SS data");
    } finally { setLoading(false); }
  }, [params, sortBy, sortDir, scSort, page, pageSize, platform, f.date_from, f.date_to]);

  useEffect(() => { load(); }, [load]);

  const assignVendor = async (vendor) => {
    setAssigning(vendor);
    try {
      const r = await api.post("/qcom-ss/assign-vendor", { vendor_name: vendor, ss_id: assign[vendor] });
      toast.success(`"${vendor}" now belongs to ${r.data.ss_name}`);
      setAssign(a => ({ ...a, [vendor]: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign the vendor name");
    } finally { setAssigning(""); }
  };

  // PASS 2 ZIP 1 FIX-UP (Fix 1c) — a Needs-mapping row saves the WAREHOUSE mapping, not just
  // the vendor name: every facility of that vendor's unresolved POs is pointed at the chosen SS.
  const saveNeedsMapping = async () => {
    const c = nmConfirm;
    if (!c) return;
    setAssigning(c.vendor);
    try {
      const r = await api.post("/qcom-ss/assign-needs-mapping", { vendor_name: c.vendor, ss_id: c.ss_id });
      toast.success(`"${c.vendor}" at ${(r.data.mapped || []).length} warehouse(s) now maps to ${r.data.ss_name}`);
      setAssign(a => ({ ...a, [c.vendor]: "" }));
      setNmConfirm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not map the warehouses");
    } finally { setAssigning(""); }
  };

  const sendAlertNow = async () => {
    setSendingAlert(true);
    try {
      const r = await api.post("/qcom-ss/alert/no-appointment/send-now");
      toast.success(r.data.pos
        ? `Sent: ${r.data.pos} PO(s) expiring within 3 days with no appointment, to ${(r.data.to || []).length} recipient(s)`
        : "Nothing to send — no open PO expires within 3 days without an appointment");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the alert");
    } finally { setSendingAlert(false); }
  };

  const sort = (col) => {
    if (sortBy === col) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
    setPage(1);
  };
  const sortSc = (col) => setScSort(s => (s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "desc" }));

  const saveTargets = async () => {
    try {
      const { data } = await api.put("/qcom-ss/targets", {
        fill_target: Number(tForm.fill_target), appointment_target: Number(tForm.appointment_target),
        dn_pct_ceiling: Number(tForm.dn_pct_ceiling), watch_from: Number(tForm.watch_from) });
      toast.success(`Targets saved — fill ${data.fill_target}%, appointment ${data.appointment_target}%, DN ceiling ${data.dn_pct_ceiling}%, watch from ${data.watch_from}%`);
      setShowTargets(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the targets"); }
  };

  const exportUrl = (what) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const qs = new URLSearchParams(params).toString();
    return `${base}/api/qcom-ss/export/${what}?${qs}`;
  };

  const SortTh = ({ col, children, right, onClick, active, dir }) => (
    <th className={`cursor-pointer select-none ${right ? "text-right" : ""}`} onClick={onClick}
      data-testid={`qcom-sort-${col}`}>
      {children}{active ? (dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
  const PoTh = (p) => <SortTh {...p} onClick={() => sort(p.col)} active={sortBy === p.col} dir={sortDir} />;
  const ScTh = (p) => <SortTh {...p} onClick={() => sortSc(p.col)} active={scSort.by === p.col} dir={scSort.dir} />;

  const selectedSs = f.ss_ids ? f.ss_ids.split(",") : [];
  const chips = f.chips ? f.chips.split(",") : [];
  const activeCount = FKEYS.filter(k => !["date_from", "date_to"].includes(k) && f[k]).length;

  return (
    <div data-testid="qcom-ss-pos">
      {/* ── one filter bar above both tabs ── */}
      <div className="border border-zinc-200 bg-zinc-50/60 rounded-sm p-3 mb-3" data-testid="qcom-filter-bar">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="PO date from">
            <input type="date" value={f.date_from} onChange={e => setF({ date_from: e.target.value })} className={inputCls} data-testid="qcom-date-from" />
          </Field>
          <Field label="to">
            <input type="date" value={f.date_to} onChange={e => setF({ date_to: e.target.value })} className={inputCls} data-testid="qcom-date-to" />
          </Field>
          <Field label="City">
            <select value={f.city} onChange={e => setF({ city: e.target.value })} className={inputCls} data-testid="qcom-city">
              <option value="">All cities</option>
              {options.cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Platform warehouse">
            <select value={f.facility} onChange={e => setF({ facility: e.target.value })} className={inputCls} data-testid="qcom-facility">
              <option value="">All warehouses</option>
              {options.facilities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Item">
            <select value={f.item} onChange={e => setF({ item: e.target.value })} className={inputCls} data-testid="qcom-item">
              <option value="">All items</option>
              {options.items.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Search PO, SS, facility or SKU">
            <input value={f.q} onChange={e => setF({ q: e.target.value })} placeholder="PO number…" className={`${inputCls} w-[190px]`} data-testid="qcom-search" />
          </Field>
          <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs" onClick={() => setMore(m => !m)} data-testid="qcom-more-filters">
            <SlidersHorizontal size={12} className="mr-1" /> More filters{activeCount ? ` (${activeCount})` : ""}
          </Button>
          <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs" onClick={clearFilters} data-testid="qcom-clear-filters">
            <X size={12} className="mr-1" /> Clear filters
          </Button>
          {/* NEXT BUILD item 9(b): closed Super Stockists stay out of every figure unless this is on */}
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer select-none" data-testid="qcom-show-closed-ss">
            <input type="checkbox" checked={f.show_closed_ss === "1"}
              onChange={e => setF({ show_closed_ss: e.target.checked ? "1" : "" })}
              className="h-3.5 w-3.5 accent-zinc-900" data-testid="qcom-show-closed-ss-input" />
            Show closed SS
          </label>
          <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs" onClick={load} data-testid="qcom-refresh">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </Button>
          {can.export && (
            <>
              <a href={exportUrl("pos")} data-testid="qcom-export-pos">
                <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs"><Download size={12} className="mr-1" /> POs (Excel)</Button>
              </a>
              <a href={exportUrl("scorecard")} data-testid="qcom-export-scorecard">
                <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs"><Download size={12} className="mr-1" /> Scorecard (Excel)</Button>
              </a>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="qcom-chips">
          {CHIPS.map(([key, label]) => (
            <button key={key} onClick={() => toggleChip(key)} data-testid={`qcom-chip-${key}`}
              className={`px-2 py-1 text-[10px] rounded-full border transition-colors ${chips.includes(key)
                ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
              {label}
            </button>
          ))}
        </div>

        {more && (
          <div className="mt-3 pt-3 border-t border-zinc-200" data-testid="qcom-more-panel">
            <div className="mb-2">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Qcom Super Stockist</p>
              <div className="flex flex-wrap gap-1.5">
                {options.super_stockists.map(s => (
                  <button key={s.id} onClick={() => toggleSs(s.id)} data-testid={`qcom-ss-filter-${s.id}`}
                    className={`px-2 py-1 text-[10px] rounded-sm border ${selectedSs.includes(s.id)
                      ? "bg-blue-600 text-white border-blue-600" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Field label="Appointment from"><input type="date" value={f.appt_from} onChange={e => setF({ appt_from: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-appt-from" /></Field>
              <Field label="Appointment to"><input type="date" value={f.appt_to} onChange={e => setF({ appt_to: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-appt-to" /></Field>
              <Field label="Expiry from"><input type="date" value={f.expiry_from} onChange={e => setF({ expiry_from: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-expiry-from" /></Field>
              <Field label="Expiry to"><input type="date" value={f.expiry_to} onChange={e => setF({ expiry_to: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-expiry-to" /></Field>
              <Field label="Status">
                <select value={f.status} onChange={e => setF({ status: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-status">
                  <option value="">All</option>
                  <option value="open">Open (not delivered)</option>
                  <option value="closed">Closed (Fulfilled or Expired)</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="Fulfilled">Fulfilled</option>
                  <option value="Expired">Expired</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Unscheduled">Unscheduled</option>
                </select>
              </Field>
              <Field label="GRN from"><input type="date" value={f.grn_from} onChange={e => setF({ grn_from: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-grn-from" /></Field>
              <Field label="GRN to"><input type="date" value={f.grn_to} onChange={e => setF({ grn_to: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-grn-to" /></Field>
              <Field label="PO value min ₹"><input type="number" value={f.value_min} onChange={e => setF({ value_min: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-value-min" /></Field>
              <Field label="PO value max ₹"><input type="number" value={f.value_max} onChange={e => setF({ value_max: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-value-max" /></Field>
              <Field label="Fill % min"><input type="number" value={f.fill_min} onChange={e => setF({ fill_min: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-fill-min" /></Field>
              <Field label="Fill % max"><input type="number" value={f.fill_max} onChange={e => setF({ fill_max: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-fill-max" /></Field>
              <Field label="Invoice status">
                <select value={f.invoice_status} onChange={e => setF({ invoice_status: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-invoice-status">
                  <option value="">Any</option><option value="Invoiced">Invoiced</option><option value="Not invoiced">Not invoiced</option>
                </select>
              </Field>
              <Field label="Payment status">
                <select value={f.payment_status} onChange={e => setF({ payment_status: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-payment-status">
                  <option value="">Any</option><option value="paid">Paid</option><option value="unpaid">Not paid</option>
                </select>
              </Field>
              <Field label="Grade">
                <select value={f.grade} onChange={e => setF({ grade: e.target.value })} className={`${inputCls} w-full`} data-testid="qcom-grade">
                  <option value="">Any</option>{["Healthy", "Watch", "Critical", "Dead"].map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Rows per page">
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className={`${inputCls} w-full`} data-testid="qcom-page-size">
                  {[5, 10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
            </div>
          </div>
        )}
      </div>

      {/* ── banners ── */}
      {widgets && (widgets.duplicate_ss_names || []).length > 0 && (
        <div className="mb-3 p-3 border border-amber-300 bg-amber-50 rounded-sm text-[11px] text-amber-900" data-testid="qcom-duplicate-ss">
          {widgets.duplicate_ss_names.map((g, i) => (
            <div key={i}>Two Super Stockists share one company name: {g.join(" and ")} — check the customer master; ORBIT keeps them separate.</div>
          ))}
        </div>
      )}
      {widgets && (widgets.unmatched_vendor_count > 0 || (widgets.needs_mapping_vendors || []).length > 0) && (
        <div className="mb-3 p-3 border border-red-300 bg-red-50 rounded-sm text-[11px] text-red-900" data-testid="qcom-unmatched">
          {widgets.unmatched_vendor_count > 0 && (widgets.unmatched_vendors || []).map(v => (
            <div key={v} className="flex flex-wrap items-center gap-2 mb-1">
              <span>Unmatched vendor: <b>{v}</b> — no Super Stockist carries this platform vendor name.</span>
              {can.manage_targets && (
                <>
                  <select value={assign[v] || ""} onChange={e => setAssign(a => ({ ...a, [v]: e.target.value }))}
                    className="h-7 border border-red-300 rounded-sm px-1.5 text-[11px] bg-white"
                    data-testid={`qcom-assign-select-${v}`}>
                    <option value="">Assign to a Super Stockist…</option>
                    {/* PASS 2 (B9): every Super Stockist, closed and inactive ones included */}
                    {(options.all_super_stockists || options.super_stockists || []).filter(s => s.id && s.id !== "UNMATCHED").map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]"
                    disabled={!assign[v] || assigning === v}
                    onClick={() => assignVendor(v)} data-testid={`qcom-assign-save-${v}`}>
                    {assigning === v ? "Saving…" : "Save"}
                  </Button>
                </>
              )}
            </div>
          ))}
          {(widgets.needs_mapping_vendors || []).map(v => {
            const det = (widgets.needs_mapping_details || []).find(x => x.vendor === v) || {};
            const chosen = (options.all_super_stockists || []).find(s => s.id === assign[v]);
            return (
              <div key={v} className="mb-1" data-testid={`qcom-needs-mapping-${v}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span>Needs mapping: <b>{v}</b>
                    {(det.shared_by || []).length > 0 && <> — shared by: <b>{det.shared_by.join(", ")}</b></>}
                    {(det.facilities || []).length > 0 && <> · at: <b>{det.facilities.join(", ")}</b></>}
                  </span>
                  {can.manage_targets && (
                    <>
                      <select value={assign[v] || ""} onChange={e => { setAssign(a => ({ ...a, [v]: e.target.value })); setNmConfirm(null); }}
                        className="h-7 border border-red-300 rounded-sm px-1.5 text-[11px] bg-white"
                        data-testid={`qcom-needs-mapping-select-${v}`}>
                        <option value="">Assign to a Super Stockist…</option>
                        {(options.all_super_stockists || options.super_stockists || []).filter(s => s.id && s.id !== "UNMATCHED").map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]"
                        disabled={!assign[v] || assigning === v}
                        onClick={() => setNmConfirm({ vendor: v, ss_id: assign[v],
                          ss_name: chosen?.name || assign[v], facilities: det.facilities || [] })}
                        data-testid={`qcom-needs-mapping-save-${v}`}>
                        {assigning === v ? "Saving…" : "Save"}
                      </Button>
                    </>
                  )}
                </div>
                {nmConfirm && nmConfirm.vendor === v && (
                  <div className="mt-1 p-2 border border-red-300 bg-white rounded-sm" data-testid={`qcom-needs-mapping-confirm-${v}`}>
                    <p>This maps warehouses <b>{(nmConfirm.facilities || []).join(", ") || "—"}</b> to <b>{nmConfirm.ss_name}</b>. Any other shared vendor name at these warehouses will also go to <b>{nmConfirm.ss_name}</b>.</p>
                    <div className="flex gap-2 mt-1">
                      <Button size="sm" className="h-7 rounded-sm text-[11px]" disabled={assigning === v}
                        onClick={saveNeedsMapping} data-testid={`qcom-needs-mapping-confirm-save-${v}`}>
                        {assigning === v ? "Saving…" : "Map the warehouses"}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]"
                        onClick={() => setNmConfirm(null)} data-testid={`qcom-needs-mapping-confirm-cancel-${v}`}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── tabs ── */}
      <div className="flex gap-1 border-b border-zinc-200 mb-4" data-testid="qcom-tabs">
        {[["pos", "POs"], ["scorecard", "SS Scorecard"]].map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); const n = new URLSearchParams(sp); n.set("qtab", k); setSp(n); }}
            className={`px-4 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === k
              ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}
            data-testid={`qcom-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "pos" && widgets && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <Card label="POs issued" value={widgets.pos_issued} sub={`${money(widgets.ordered)} ordered${widgets.cancelled_count ? ` · ${widgets.cancelled_count} cancelled (${money(widgets.cancelled_value)})` : ""}`} testId="qcom-w-issued" />
            <Card label="Not delivered yet" value={widgets.not_delivered_count} sub={`${money(widgets.open_value)} open`} tone="text-amber-700" testId="qcom-w-open" />
            <Card label="Expiring in 3 days" value={widgets.expiring_count} sub={money(widgets.expiring_value)} tone="text-red-700" testId="qcom-w-expiring" />
            <Card label="No appointment" value={widgets.no_appointment_count} sub="open POs" tone="text-red-700" testId="qcom-w-no-appt" />
            <Card label="Appointment missed" value={widgets.appointment_missed_count} sub="no GRN after the slot" tone="text-red-700" testId="qcom-w-missed" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Card label="GRN'd" value={money(widgets.grn_value)} sub={`Fill ${widgets.fill_pct}% of ${money(widgets.closed_ordered)} on closed POs`} tone="text-emerald-700" testId="qcom-w-grn" />
            <Card label={widgets.lost_label} value={money(widgets.lost_on_expired)}
              sub={widgets.legacy_closed_count && !widgets.expired_count
                ? `${widgets.legacy_closed_count} closed PO(s) from old uploads`
                : `${widgets.expired_count} expired PO(s) · ${widgets.expired_nothing_received} with nothing received`}
              tone="text-red-700" testId="qcom-w-lost" />
            <Card label="Short GRN" value={money(widgets.short_grn)} sub={`${widgets.short_grn_count} fulfilled PO(s)`} tone="text-amber-700" testId="qcom-w-short" />
            <Card label="Price mismatch at GRN" value={widgets.price_mismatch_lines}
              sub={can.values ? `line(s) · ${money(Math.abs(widgets.price_mismatch_value || 0))} paid ${Number(widgets.price_mismatch_value || 0) >= 0 ? "less" : "more"}` : "line(s)"}
              tone="text-orange-700" testId="qcom-w-mismatch" />
            {can.values && <Card label="Returns (PRN)" value={money(widgets.returns_prn)} sub={`${widgets.returns_prn_lines || 0} line(s) returned`} tone="text-red-700" testId="qcom-w-prn" />}
            {can.payments && <Card label="Platform money" value={money(widgets.paid)} sub={`paid · ${money(widgets.approved_not_paid)} approved not paid · ${money(widgets.debit_notes)} DN`} testId="qcom-w-money" />}
          </div>

          <h3 className="text-xs font-semibold text-zinc-600 mb-1">Purchase orders{loading ? " — loading…" : ""}</h3>
          <Pager data={pos} page={page} setPage={setPage} testId="qcom-pager-top" />
          <div className="border border-zinc-200 overflow-x-auto" data-testid="qcom-po-table">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th></th>
                  <PoTh col="po_number">PO #</PoTh>
                  <PoTh col="platform">Platform</PoTh>
                  <PoTh col="ss_name">Super Stockist</PoTh>
                  <PoTh col="facility_name">Facility</PoTh>
                  <PoTh col="po_date">PO date</PoTh>
                  <PoTh col="appointment_date">Appointment</PoTh>
                  <PoTh col="expiry_date">Expiry</PoTh>
                  <PoTh col="po_status">Status</PoTh>
                  <th className="text-right">Items</th>
                  {can.values && <PoTh col="ordered" right>Ordered ₹</PoTh>}
                  {can.values && <PoTh col="grn" right>GRN ₹</PoTh>}
                  <PoTh col="fill_pct" right>Fill %</PoTh>
                  <PoTh col="invoice_number">Invoice</PoTh>
                  {can.payments && <PoTh col="paid_to_ss" right>Paid to SS ₹</PoTh>}
                </tr>
              </thead>
              <tbody>
                {(pos?.rows || []).length === 0 && !loading && (
                  <tr><td colSpan={15} className="text-center text-xs text-zinc-400 py-6">No POs match these filters.</td></tr>
                )}
                {(pos?.rows || []).map(p => (
                  <Fragment key={`${p.platform}-${p.po_number}`}>
                    <tr className={p.expired ? "bg-red-50" : ""}>
                      <td>
                        <button onClick={() => setOpen(o => ({ ...o, [p.po_number]: !o[p.po_number] }))}
                          className="text-zinc-500 hover:text-zinc-900" data-testid={`qcom-expand-${p.po_number}`}>
                          {open[p.po_number] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                      </td>
                      <td className="font-mono text-[11px] font-semibold">{p.po_number}</td>
                      <td className="text-[11px] capitalize">{p.platform}</td>
                      <td className="text-xs">
                        {p.ss_id && p.ss_id !== "DIRECT_NO_SS"
                          ? <button className="text-blue-700 hover:underline" onClick={() => navigate(`/qcom-ss/${p.ss_id}`)}
                              data-testid={`qcom-ss-link-${p.po_number}`}>{p.ss_name}</button>
                          : <span className={["unmatched", "needs_mapping"].includes(p.match_type) ? "text-red-700" : ""}>{p.ss_name}</span>}
                        <span className="block text-[9px] text-zinc-400">{p.vendor_name}</span>
                      </td>
                      <td className="text-xs">{p.facility_name}<span className="block text-[9px] text-zinc-400">{p.city}</span></td>
                      <td className="text-[11px]">{fmtDate(p.po_date)}</td>
                      <td className="text-[11px]">{p.appointment_date ? fmtDate(p.appointment_date) : <span className="text-red-600">none</span>}</td>
                      <td className="text-[11px]">{fmtDate(p.expiry_date)}{p.expiring_soon ? <span className="block text-[9px] text-red-600">in {p.days_to_expiry}d</span> : null}</td>
                      <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm border ${p.cancelled ? "bg-zinc-100 text-zinc-600 border-zinc-300" : p.closed ? (p.expired ? "bg-red-100 text-red-700 border-red-300" : "bg-emerald-100 text-emerald-700 border-emerald-300") : "bg-blue-50 text-blue-700 border-blue-200"}`} data-testid={`qcom-state-${p.po_number}`}>{p.state_label || p.po_state_raw || p.po_status || "—"}</span></td>
                      <td className="text-right text-[11px]">{p.items_count}</td>
                      {can.values && <td className="text-right font-mono text-[11px]">{money(p.ordered)}</td>}
                      {can.values && <td className="text-right font-mono text-[11px]">{money(p.grn)}</td>}
                      <td className="text-right font-mono text-[11px]">{p.fill_pct}%</td>
                      <td className="text-[10px]">
                        {p.invoice_number || "—"}
                        <span className={`block text-[9px] ${p.invoice_status === "Invoiced" ? "text-emerald-700" : "text-zinc-400"}`}>{p.invoice_status}</span>
                      </td>
                      {can.payments && <td className="text-right font-mono text-[11px]">{money(p.paid_to_ss)}</td>}
                    </tr>
                    {open[p.po_number] && p.items.map((it, i) => (
                      <tr key={`${p.po_number}-${i}`} className="bg-zinc-50" data-testid={`qcom-item-${p.po_number}-${i}`}>
                        <td></td>
                        <td colSpan={3} className="text-[11px] pl-4">
                          {it.item_name}
                          <span className="block text-[9px] text-zinc-400">item {it.item_id || "—"} · UPC {it.upc || "—"} · {it.uom_text || "—"}</span>
                        </td>
                        <td colSpan={6} className="text-[10px] text-zinc-500">
                          {it.ordered_qty} ordered · {it.received_qty} GRN'd{can.values ? ` · rate ${money(it.landing_price)} · MRP ${money(it.mrp)}` : ""}
                          {it.price_mismatch && <span className={Number(it.price_mismatch_value || 0) >= 0 ? "text-red-700" : "text-emerald-700"} data-testid={`qcom-mismatch-${p.po_number}`}> · GRN at {money(it.grn_landing_price)} — Blinkit paid {Number(it.price_mismatch_value || 0) >= 0 ? "less" : "more"} {money(Math.abs(Number(it.price_mismatch_value || 0)))}</span>}
                        </td>
                        {can.values && <td className="text-right font-mono text-[11px]">{money(it.ordered)}</td>}
                        {can.values && <td className="text-right font-mono text-[11px]">{money(it.grn)}</td>}
                        <td colSpan={can.payments ? 3 : 2}></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <Pager data={pos} page={page} setPage={setPage} testId="qcom-pager-bottom" />
        </>
      )}

      {tab === "scorecard" && score && (
        <div data-testid="qcom-scorecard">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-zinc-600">
              Healthy from {score.targets.fill_target}% fill · Watch from {score.targets.watch_from}% ·
              appointment target {score.targets.appointment_target}% · debit notes under {score.targets.dn_pct_ceiling}% of GRN
              {score.prev_month ? ` · change measured against ${score.prev_month}` : ""}
            </h3>
            {canManageTargets && can.manage_targets !== false && (
              <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]"
                onClick={() => setShowTargets(true)} data-testid="qcom-set-targets">
                <Target size={12} className="mr-1" /> Set targets
              </Button>
            )}
            {can.admin && (
              <Button variant="outline" size="sm" className="h-7 rounded-sm text-[11px]"
                disabled={sendingAlert} onClick={sendAlertNow} data-testid="qcom-send-alert-now">
                {sendingAlert ? "Sending…" : "Send now (test) — no-appointment alert"}
              </Button>
            )}
          </div>
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="data-table w-full">
              <thead><tr>
                <ScTh col="ss_name">Super Stockist</ScTh>
                <ScTh col="pos" right>POs</ScTh>
                {can.values && <ScTh col="ordered" right>Ordered ₹</ScTh>}
                {can.values && <ScTh col="closed_ordered" right>Closed ₹</ScTh>}
                {can.values && <ScTh col="grn" right>GRN ₹</ScTh>}
                <ScTh col="fill_pct" right>Fill %</ScTh>
                <ScTh col="fill_change" right>vs last month</ScTh>
                <ScTh col="appointment_within_2_pct" right>Appt ≤ 2 days %</ScTh>
                <ScTh col="avg_days_po_to_grn" right>Avg days PO→GRN</ScTh>
                <ScTh col="dn_pct_of_grn" right>DN % of GRN</ScTh>
                {can.payments && <ScTh col="paid_by_platform" right>Paid by platform ₹</ScTh>}
                {can.values && <ScTh col="lost" right>Lost ₹</ScTh>}
                <ScTh col="appointments_missed" right>Appt missed</ScTh>
                <ScTh col="grade">Grade</ScTh>
                <th></th>
              </tr></thead>
              <tbody>
                {score.rows.map((r, i) => (
                  <tr key={i} data-testid={`qcom-score-row-${i}`}>
                    <td className="text-xs font-medium">{r.ss_name}</td>
                    <td className="text-right text-[11px]">{r.pos}</td>
                    {can.values && <td className="text-right font-mono text-[11px]">{money(r.ordered)}</td>}
                    {can.values && <td className="text-right font-mono text-[11px]">{money(r.closed_ordered)}</td>}
                    {can.values && <td className="text-right font-mono text-[11px]">{money(r.grn)}</td>}
                    <td className="text-right font-mono text-[11px]">{r.fill_pct}%</td>
                    <td className={`text-right font-mono text-[11px] ${r.fill_change > 0 ? "text-emerald-700" : r.fill_change < 0 ? "text-red-700" : "text-zinc-400"}`}
                      data-testid={`qcom-score-change-${i}`}>
                      {r.fill_change === null || r.fill_change === undefined ? "—" : `${r.fill_change > 0 ? "+" : ""}${r.fill_change} pts`}
                    </td>
                    <td className={`text-right font-mono text-[11px] ${r.appointment_within_2_pct < score.targets.appointment_target ? "text-red-700 font-semibold" : ""}`}
                      data-testid={`qcom-score-appt-${i}`}>{r.appointment_within_2_pct}%</td>
                    <td className="text-right font-mono text-[11px]">{r.avg_days_po_to_grn === null ? "—" : r.avg_days_po_to_grn}</td>
                    <td className={`text-right font-mono text-[11px] ${r.dn_pct_of_grn > score.targets.dn_pct_ceiling ? "text-red-700 font-semibold" : ""}`}
                      data-testid={`qcom-score-dn-${i}`}>{r.dn_pct_of_grn}%</td>
                    {can.payments && <td className="text-right font-mono text-[11px]">{money(r.paid_by_platform)}</td>}
                    {can.values && <td className="text-right font-mono text-[11px]">{money(r.lost)}</td>}
                    <td className="text-right text-[11px]">{r.appointments_missed}</td>
                    <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm border ${GRADE[r.grade]}`} data-testid={`qcom-grade-${i}`}>{r.grade}</span></td>
                    <td>
                      {r.ss_id && r.ss_id !== "DIRECT_NO_SS" && (
                        <button className="text-[10px] text-blue-700 hover:underline"
                          onClick={() => navigate(`/qcom-ss/${r.ss_id}`)} data-testid={`qcom-open-ss-${i}`}>Open</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showTargets && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="qcom-targets-dialog">
          <div className="bg-white rounded-sm p-5 w-[400px] border border-zinc-200">
            <h3 className="text-sm font-semibold mb-1">Set Qcom SS targets</h3>
            <p className="text-[11px] text-zinc-500 mb-4">The grade uses fill % only. The other two targets colour their own column red when missed.</p>
            {[["fill_target", "Healthy from (fill %)", "qcom-target-fill"],
              ["appointment_target", "Appointment within 2 days (%)", "qcom-target-appointment"],
              ["dn_pct_ceiling", "Debit notes must stay under (% of GRN)", "qcom-target-dn"],
              ["watch_from", "Watch from (fill %)", "qcom-target-watch"]].map(([key, label, tid]) => (
              <div key={key}>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{label}</label>
                <input type="number" value={tForm[key]} onChange={e => setTForm(t => ({ ...t, [key]: e.target.value }))}
                  className="w-full h-8 text-xs border border-zinc-200 rounded-sm px-2 mb-3" data-testid={tid} />
              </div>
            ))}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setShowTargets(false)} data-testid="qcom-targets-cancel">Cancel</Button>
              <Button size="sm" className="rounded-sm text-xs" onClick={saveTargets} data-testid="qcom-targets-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
